import { faCalendarAlt, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import React, { useContext, useEffect } from "react";
import { AppContext, BookingData, ServiceDuration, TimeSlot } from "../../../../AppDataProvider/AppDataProvider";
import { dateFormats, dateTimeFormats, gap, marginBottom, marginRight, media, timeFormats, typography } from "../../../../common/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { differenceInDays } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ActionType } from "../../../../AppDataProvider/actions";
import { SelectableButton } from "../../../../common/components";
import { applyOpacity } from "../../../../common/styles/utils";
import { getRecurringDescription } from "../../../../utils";
import { PartRequired } from "../../../../utils/types";
import { useGetTimeSlots } from "../../hooks/useGetTimeSlots";
import { RecurringSlotInfo } from "./RecurringSlotInfo/RecurringSlotInfo";
export type TimeSlotSelectionProps = {
  attendees: number;
  duration: ServiceDuration;
  /**
   * Callback function which is called when a time slot is selected.
   */
  onSelectTimeSlot: (TimeSlot: TimeSlot) => void;
  onTimeSlotsLoaded: (timeSlots: boolean) => void;
  /**
   * The date which has been selected from the calendar date picker.
   */
  selectedDate: Date;
  /**
   * The currently selected timeslot (optional)
   */
  selectedTimeSlot?: TimeSlot | null;
};
export function TimeSlotSelection(props: TimeSlotSelectionProps): React.ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    attendees,
    duration,
    selectedDate,
    selectedTimeSlot,
    onSelectTimeSlot,
    onTimeSlotsLoaded
  } = props;
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  type TimeSlotSelectionBookingData = PartRequired<BookingData, "duration" | "service" | "timezone">;
  const {
    timezone,
    service
  } = (appData.bookingData as TimeSlotSelectionBookingData);
  const {
    timeSlots,
    loading,
    error
  } = useGetTimeSlots({
    attendees,
    bookingData: appData.bookingData,
    bookingTool: appData.bookingTool,
    duration,
    selectedDate
  });
  useEffect(() => {
    if (error) {
      appDataDispatch({
        payload: {
          message: t("errors.availabilityRequestFailed"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
    }
  }, [appDataDispatch, error, t]);
  useEffect(() => {
    if (!loading && timeSlots.length >= 0) {
      onTimeSlotsLoaded(timeSlots.length > 0);
    }
  }, [onTimeSlotsLoaded, loading, timeSlots]);
  const selectTimeSlot = (timeSlot: TimeSlot) => {
    onSelectTimeSlot(timeSlot);
  };

  // Simplest way to keep the layout as intended is to always render the
  // Container, whether or not we've finished fetching timeslots yet.
  if (timeSlots.length === 0) return <Container />;
  return <Container data-sentry-element="Container" data-sentry-component="TimeSlotSelection" data-sentry-source-file="TimeSlotSelection.tsx">
      <SelectedDate data-sentry-element="SelectedDate" data-sentry-source-file="TimeSlotSelection.tsx">
        <CalendarIcon data-sentry-element="CalendarIcon" data-sentry-source-file="TimeSlotSelection.tsx" />
        {selectedDate.toLocaleString(i18n.language, dateFormats.default)}
      </SelectedDate>
      {selectedTimeSlot && selectedTimeSlot.interval.__typename === "GroupBookingInterval" && selectedTimeSlot.interval.booking.recurring && <RecurringSlotInfo recurrence={selectedTimeSlot.interval.booking.recurring} startDate={selectedTimeSlot.interval.start} />}
      <TimeSlotsContainer data-sentry-element="TimeSlotsContainer" data-sentry-source-file="TimeSlotSelection.tsx">
        {timeSlots.map(timeSlot => {
        const {
          interval
        } = timeSlot;
        const {
          start,
          end
        } = interval;
        const startDate = new Date(start);
        const endDate = new Date(end);
        const isLongService = differenceInDays(endDate, startDate) >= 1;
        const isRecurringBookingSlot = interval.__typename === "GroupBookingInterval" && interval.booking && interval.booking.recurring;
        const displayStartTime = startDate.toLocaleString(i18n.language, {
          ...timeFormats.default,
          timeZone: timezone
        });

        // Have to use explicit conditions rather than the isRecurringBookingSlot
        // variable, because typescript isn't smart enough to know what type
        // interval will be if we abstract the checks into a variable.
        const buttonAriaLabel = interval.__typename === "GroupBookingInterval" && interval.booking && interval.booking.recurring ? getRecurringDescription({
          locale: i18n.language,
          recurrence: interval.booking.recurring,
          service,
          startDate: interval.booking.start,
          t
        }) : startDate.toLocaleString(i18n.language, {
          ...dateTimeFormats.full,
          timeZone: timezone
        });
        const isSelected = selectedTimeSlot?.interval.start === start;
        return <SelectableButton key={start} selected={isSelected} aria-label={buttonAriaLabel} onClick={() => {
          selectTimeSlot(timeSlot);
        }}>
              {isRecurringBookingSlot && <RecurringSlotIcon />}
              {isLongService ? <span style={{
            textTransform: "capitalize"
          }}>
                  {t("available")}
                </span> : displayStartTime}
            </SelectableButton>;
      })}
      </TimeSlotsContainer>
    </Container>;
}
const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: min-content;
  @media ${media.mobile} {
    width: 100%;
  }
`;
const SelectedDate = styled.div`
  display: flex;
  flex-direction: row;

  ${typography.body}
  ${marginBottom.m}
  ${gap.s}
`;
const CalendarIcon = styled(FontAwesomeIcon).attrs({
  icon: faCalendarAlt
})`
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.4)};

  margin-top: auto;
  margin-bottom: auto;
`;
const TimeSlotsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(6rem, 100%/3), 1fr));
  grid-auto-rows: 2.5rem;
  grid-gap: 0.25rem;
`;
const RecurringSlotIcon = styled(FontAwesomeIcon).attrs({
  icon: faSyncAlt
})`
  ${marginRight.s}
`;